import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Award } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';

interface LevelInfo {
  name: string;
  color_primary: string;
  color_secondary: string;
}

const DEFAULT_LEVEL: LevelInfo = {
  name: 'Starter',
  color_primary: '#6366f1',
  color_secondary: '#818cf8',
};

function parseHexColor(hex: string): [number, number, number] | null {
  const h = hex.replace('#', '').trim();
  if (h.length === 3) {
    return [
      parseInt(h[0] + h[0], 16),
      parseInt(h[1] + h[1], 16),
      parseInt(h[2] + h[2], 16),
    ];
  }
  if (h.length === 6) {
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  }
  return null;
}

function getLuminance(hex: string): number {
  const rgb = parseHexColor(hex);
  if (!rgb) return 0.5;
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function darkenHex(hex: string, amount = 0.18): string {
  const rgb = parseHexColor(hex);
  if (!rgb) return hex;
  return `#${rgb.map((v) => Math.max(0, Math.round(v * (1 - amount))).toString(16).padStart(2, '0')).join('')}`;
}

function getBadgeColors(primary: string, secondary: string) {
  const secondaryTooLight = getLuminance(secondary) > 0.55;
  const gradientEnd = secondaryTooLight ? darkenHex(primary, 0.22) : secondary;
  const background = `linear-gradient(135deg, ${primary} 0%, ${gradientEnd} 100%)`;
  const referenceLum = secondaryTooLight ? getLuminance(primary) : Math.min(getLuminance(primary), getLuminance(gradientEnd));
  const color = referenceLum > 0.52 ? '#422006' : '#ffffff';
  return { background, color };
}

const EmployeePointsBadge: React.FC = () => {
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [balance, setBalance] = useState<number | null>(null);
  const [level, setLevel] = useState<LevelInfo>(DEFAULT_LEVEL);
  const [loading, setLoading] = useState(true);

  const loadPoints = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase.rpc('get_employee_points_breakdown', {
        p_user_id: user.id,
      });
      if (error || !data || data.error) return;
      setBalance(typeof data.points_balance === 'number' ? data.points_balance : 0);
      if (data.current_level) {
        setLevel({
          name: data.current_level.name ?? DEFAULT_LEVEL.name,
          color_primary: data.current_level.color_primary ?? DEFAULT_LEVEL.color_primary,
          color_secondary: data.current_level.color_secondary ?? DEFAULT_LEVEL.color_secondary,
        });
      }
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (userRole !== 'Vendedor' && userRole !== 'Producción') return;
    loadPoints();
  }, [userRole, loadPoints, location.pathname]);

  if (userRole !== 'Vendedor' && userRole !== 'Producción') return null;

  const primary = level.color_primary;
  const secondary = level.color_secondary || primary;
  const isActive = location.pathname === '/puntos-colaboradores';
  const badgeColors = getBadgeColors(primary, secondary);

  return (
    <button
      type="button"
      onClick={() => navigate('/puntos-colaboradores')}
      title={`${level.name} · Mis Puntos`}
      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold shadow-sm transition-all hover:opacity-90 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
        isActive ? 'ring-2 ring-offset-2 dark:ring-offset-gray-800' : ''
      }`}
      style={{
        background: badgeColors.background,
        color: badgeColors.color,
        ['--tw-ring-color' as string]: primary,
      }}
    >
      <Award className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
      <span className="tabular-nums">
        {loading ? '…' : (balance ?? 0).toLocaleString('es-AR')}
      </span>
    </button>
  );
};

export default EmployeePointsBadge;
